import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Check, X } from 'lucide-react';
import { useTranscriptionProviders } from '@/hooks/useTranscriptionProviders';
import { useToast } from '@/hooks/use-toast';
import {
  CUSTOM_TRANSCRIPTION_NOTE, KEY_REENTRY_HELP, TRANSCRIPTION_PRESETS, canSaveTranscription, canTestTranscription,
  describeTranscriptionError, describeTranscriptionTest, requiresKeyReentry, sameConfig,
  type TestedConfig, type TranscriptionPresetKey, type TranscriptionProvider, type TranscriptionTestResult,
} from '@/lib/transcriptionProviderLogic';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  provider?: TranscriptionProvider;
  /** Hook actions are shared with the parent list so both stay in sync. */
  actions: Pick<ReturnType<typeof useTranscriptionProviders>, 'testProvider' | 'saveProvider'>;
}

const firstModel = (p: TranscriptionPresetKey) => TRANSCRIPTION_PRESETS[p].models[0]?.id ?? '';

export function TranscriptionProviderDialog({ open, onOpenChange, provider, actions }: Props) {
  const { toast } = useToast();
  const [label, setLabel] = useState('');
  const [preset, setPreset] = useState<TranscriptionPresetKey>('groq');
  const [baseUrl, setBaseUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [model, setModel] = useState('');
  const [test, setTest] = useState<{ result: TranscriptionTestResult; config: TestedConfig } | null>(null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    setApiKey('');
    setTest(null);
    setTesting(false);
    setSaving(false);
    setFormError(null);
    if (!open) return;
    const p: TranscriptionPresetKey = !provider ? 'groq'
      : provider.preset === 'groq' || provider.preset === 'openai' ? provider.preset : 'custom';
    setPreset(p);
    setLabel(provider?.label ?? '');
    setBaseUrl(provider?.base_url ?? TRANSCRIPTION_PRESETS.groq.baseUrl);
    setModel(provider?.default_model ?? firstModel('groq'));
  }, [open, provider]);

  const current: TestedConfig = { baseUrl, model, apiKey };
  const isEdit = !!provider;
  const fixed = preset !== 'custom';
  const models = TRANSCRIPTION_PRESETS[preset].models;
  const modelOptions = models.some((m) => m.id === model) || !model ? models : [...models, { id: model, label: model, note: 'current' }];

  const handlePreset = (v: string) => {
    const key = v as TranscriptionPresetKey;
    setPreset(key);
    setBaseUrl(TRANSCRIPTION_PRESETS[key].baseUrl);
    setModel(firstModel(key));
    setTest(null);
  };

  const handleTest = async () => {
    setTesting(true);
    setFormError(null);
    const config = { ...current };
    try {
      const result = await actions.testProvider({
        id: provider?.id, baseUrl: baseUrl.trim(), apiKey: apiKey || undefined, defaultModel: model.trim(),
      });
      setTest({ result, config });
    } catch (e) {
      setTest(null);
      setFormError((e as Error).message);
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!test?.result.testToken) return;
    setSaving(true);
    setFormError(null);
    try {
      await actions.saveProvider({
        id: provider?.id, label: label.trim(), preset, baseUrl: baseUrl.trim(), apiKey: apiKey || undefined,
        defaultModel: model.trim(), testToken: test.result.testToken,
      });
      toast({ title: provider ? `Updated ${label}` : `Added ${label}` });
      setApiKey('');
      setTest(null);
      onOpenChange(false);
    } catch (e) {
      const err = e as Error & { code?: string };
      setFormError(describeTranscriptionError(err.code, err.message));
    } finally {
      setSaving(false);
    }
  };

  const shown = test ? describeTranscriptionTest(test.result) : null;
  const dirty = !!test && !sameConfig(test.config, current);
  const needsKey = requiresKeyReentry({ isEdit, baseUrl, storedBaseUrl: provider?.base_url, apiKey });
  const testable = canTestTranscription({ baseUrl, model, apiKey, isEdit, storedBaseUrl: provider?.base_url });
  const handleOpenChange = (o: boolean) => {
    if (!o && (saving || testing)) return;
    onOpenChange(o);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit transcription provider' : 'Add transcription provider'}</DialogTitle>
          <DialogDescription>
            Speech-to-text used by meeting capture and the Ask Masira microphone. Test it before saving.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="tr-label">Label</Label>
            <Input id="tr-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Groq Whisper" />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="tr-preset">Preset</Label>
            <Select value={preset} onValueChange={handlePreset}>
              <SelectTrigger id="tr-preset"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(Object.keys(TRANSCRIPTION_PRESETS) as TranscriptionPresetKey[]).map((k) => (
                  <SelectItem key={k} value={k}>{TRANSCRIPTION_PRESETS[k].label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="tr-base-url">Base URL</Label>
            <Input id="tr-base-url" value={baseUrl} onChange={(e) => { setBaseUrl(e.target.value.trim()); setTest(null); }}
              readOnly={fixed} aria-readonly={fixed} className={fixed ? 'bg-muted/50' : undefined}
              placeholder="https://api.example.com/v1" />
            {!fixed && <p className="text-xs text-muted-foreground">{CUSTOM_TRANSCRIPTION_NOTE}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="tr-model">Model</Label>
            {fixed ? (
              <Select value={model} onValueChange={(v) => { setModel(v); setTest(null); }}>
                <SelectTrigger id="tr-model"><SelectValue placeholder="Select a model" /></SelectTrigger>
                <SelectContent>
                  {modelOptions.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      <span className="font-medium">{m.label}</span>
                      <span className="ms-2 text-xs text-muted-foreground">{m.note}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input id="tr-model" value={model} onChange={(e) => { setModel(e.target.value); setTest(null); }} placeholder="whisper-large-v3" />
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="tr-api-key">API key</Label>
            <Input id="tr-api-key" type="password" autoComplete="new-password" value={apiKey}
              onChange={(e) => { setApiKey(e.target.value); setTest(null); }}
              placeholder={provider?.api_key_last4 ? `•••• ${provider.api_key_last4}` : 'gsk_... / sk-...'}
              aria-describedby="tr-api-key-help" />
            <p id="tr-api-key-help" className="text-xs text-muted-foreground">
              {isEdit ? 'Leave blank to keep the current key. The key is write-only and is never shown again.' : 'Stored encrypted; never shown again after saving.'}
            </p>
          </div>

          <div className="space-y-2">
            <Button type="button" variant="outline" onClick={handleTest} disabled={testing || saving || !testable}
              aria-describedby={needsKey ? 'tr-key-reentry' : undefined}>
              {testing ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : null}
              Test connection
            </Button>
            {needsKey && <p id="tr-key-reentry" className="text-xs text-muted-foreground">{KEY_REENTRY_HELP}</p>}
            <div aria-live="polite" className="min-h-5 text-sm">
              {shown && (
                <div className={shown.ok ? 'text-success' : 'text-destructive'}>
                  <span className="flex items-center gap-1 font-medium">
                    {shown.ok ? <Check className="h-4 w-4" aria-hidden /> : <X className="h-4 w-4" aria-hidden />}
                    {shown.title}
                  </span>
                  {shown.detail && <span className="block text-xs">{shown.detail}</span>}
                  {shown.raw && (
                    <details className="mt-1 text-xs text-muted-foreground">
                      <summary className="cursor-pointer">Details</summary>
                      <pre className="mt-1 whitespace-pre-wrap break-all font-mono">{shown.raw}</pre>
                    </details>
                  )}
                </div>
              )}
              {dirty && <span className="block text-xs text-muted-foreground">Settings changed since the last test. Test again to enable Save.</span>}
              {formError && <span role="alert" className="block text-destructive">{formError}</span>}
            </div>
          </div>

          {isEdit && provider?.is_active && (
            <p className="rounded-md border border-warning/50 p-3 text-xs">
              This provider is active. Changes take effect within about a minute.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving || testing}>Cancel</Button>
          <Button type="button" onClick={handleSave}
            disabled={!canSaveTranscription({ label, test, current, saving, testing, isEdit, storedBaseUrl: provider?.base_url })}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
